import React, { Suspense } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../types/store';
import { UserType, usePermissions, SYSTEM_PERMISSIONS } from 'sama-shared';
import { Spin } from 'antd';
import { useFinanceLiveSync } from '@sama-partner/features/tenant/finance/hooks/useFinanceLiveSync';

const ProductCatalog = React.lazy(
  () => import('@sama-partner/features/tenant/product/views/ProductStoreCatalog')
);
const ProductMasterCatalogPage = React.lazy(
  () => import('@sama-partner/features/tenant/product/views/ProductMasterCatalog')
);
const ModifiersView = React.lazy(
  () => import('@sama-partner/features/tenant/modifier/views/ModifiersView')
);
const OrdersBoard = React.lazy(
  () => import('@sama-partner/features/tenant/orders/views/OrdersBoardDispatcher')
);
const CategoryView = React.lazy(
  () => import('@sama-partner/features/tenant/category/views/CategoryView')
);
const LocationsView = React.lazy(
  () => import('@sama-partner/features/tenant/locations/views/LocationsView')
);
const UsersView = React.lazy(() => import('@sama-partner/features/tenant/users/views/UsersView'));
const OffersView = React.lazy(
  () => import('@sama-partner/features/tenant/offers/views/OffersView')
);
const CombosView = React.lazy(
  () => import('@sama-partner/features/tenant/combos/views/CombosView')
);
const SettingsView = React.lazy(
  () => import('@sama-partner/features/tenant/settings/views/SettingsView')
);
const RolesManagementView = React.lazy(
  () => import('@sama-partner/features/tenant/users/views/RolesManagementView')
);
const AnalyticsView = React.lazy(
  () => import('@sama-partner/features/tenant/finance/views/AnalyticsView')
);
const FinanceView = React.lazy(
  () => import('@sama-partner/features/tenant/finance/views/FinanceView')
);
const PartnerDeliveryIntegrationsView = React.lazy(
  () => import('@sama-partner/features/tenant/delivery/views/DeliveryIntegrationView')
);
const OrderHistoryView = React.lazy(
  () => import('@sama-partner/features/tenant/orders/views/OrderHistoryView')
);
const LocationScheduleView = React.lazy(
  () => import('@sama-partner/features/tenant/locations/views/LocationScheduleView')
);
const PartnerSettlementsView = React.lazy(
  () => import('@sama-partner/features/tenant/finance/views/PartnerSettlementsView')
);
const PartnerInvoicesView = React.lazy(
  () => import('@sama-partner/features/tenant/finance/views/PartnerInvoicesView')
);
const MyAccountView = React.lazy(
  () => import('@sama-partner/features/tenant/account/views/MyAccountView')
);
const MySubscriptionView = React.lazy(
  () => import('@sama-partner/features/tenant/subscription/views/MySubscriptionView')
);
const PartnerInvoiceDetailView = React.lazy(
  () => import('@sama-partner/features/tenant/finance/views/PartnerInvoiceDetailView')
);

export const TenantRoute: React.FC = () => {
  const userType = useSelector((state: RootState) => state.auth.userType);
  const activeStoreId = useSelector((state: RootState) => state.auth.activeStoreId);
  const { hasPermission } = usePermissions();
  useFinanceLiveSync(
    userType === UserType.tenant && hasPermission(SYSTEM_PERMISSIONS.FINANCE_READ)
  );

  if (userType !== UserType.tenant) {
    return <Navigate to="/admin" replace />;
  }

  if (!activeStoreId) {
    return <Navigate to="/select-store" replace />;
  }

  return (
    <Suspense
      fallback={
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
          }}
        >
          <Spin size="large" />
        </div>
      }
    >
      <Routes>
        <Route path="/" element={<Navigate to="/orders" replace />} />

        {hasPermission('product:read') && <Route path="/productos" element={<ProductCatalog />} />}
        {hasPermission('product:read') && (
          <Route path="/master-catalog" element={<ProductMasterCatalogPage />} />
        )}
        {hasPermission('modifier:read') && (
          <Route path="/modificadores" element={<ModifiersView />} />
        )}
        {hasPermission('order:read') && <Route path="/orders" element={<OrdersBoard />} />}
        {hasPermission('category:read') && <Route path="/categories" element={<CategoryView />} />}
        {hasPermission(SYSTEM_PERMISSIONS.REPORTS_READ) && (
          <Route path="/analytics" element={<AnalyticsView />} />
        )}
        {hasPermission(SYSTEM_PERMISSIONS.FINANCE_READ) && (
          <Route path="/finanzas" element={<FinanceView />} />
        )}
        {hasPermission(SYSTEM_PERMISSIONS.FINANCE_READ) && (
          <Route path="/comisiones" element={<PartnerSettlementsView />} />
        )}
        {hasPermission(SYSTEM_PERMISSIONS.FINANCE_READ) && (
          <Route path="/facturas" element={<PartnerInvoicesView />} />
        )}
        {hasPermission(SYSTEM_PERMISSIONS.FINANCE_READ) && (
          <Route path="/facturas/:id" element={<PartnerInvoiceDetailView />} />
        )}
        {hasPermission('offer:read') && <Route path="/ofertas" element={<OffersView />} />}
        {hasPermission('product:read') && <Route path="/promos" element={<CombosView />} />}
        {hasPermission('location:read') && <Route path="/locations" element={<LocationsView />} />}
        {hasPermission('location:read') && (
          <Route path="/locations/horario" element={<LocationScheduleView />} />
        )}
        {hasPermission('user:read') && <Route path="/colaboradores" element={<UsersView />} />}
        {hasPermission('role:read') && <Route path="/roles" element={<RolesManagementView />} />}
        {hasPermission('partner_profile:read') && (
          <Route path="/settings" element={<SettingsView />} />
        )}
        {hasPermission('order:read') && (
          <Route path="/order/history" element={<OrderHistoryView />} />
        )}
        {hasPermission('partner_profile:read') && (
          <Route path="/delivery-integration" element={<PartnerDeliveryIntegrationsView />} />
        )}

        {/* Cuenta propia: sin permiso, cualquier colaborador ve su cuenta y el plan del negocio. */}
        <Route path="/mi-cuenta" element={<MyAccountView />} />
        <Route path="/mi-suscripcion" element={<MySubscriptionView />} />
        <Route path="*" element={<Navigate to="/orders" replace />} />
      </Routes>
    </Suspense>
  );
};

export default TenantRoute;
